import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Check, X, Trash2, MessageSquare, Reply } from "lucide-react";
import { toast } from "sonner";
import { Stars } from "@/components/store/Stars";

interface Review {
  id: string;
  product_id: string;
  user_id: string | null;
  customer_name: string;
  rating: number;
  comment: string | null;
  image_url: string | null;
  status: string;
  admin_reply: string | null;
  created_at: string;
  product?: { name: string };
}

const statusStyles: Record<string, string> = {
  pending: "bg-warning/15 text-warning-foreground",
  approved: "bg-success/15 text-success",
  rejected: "bg-destructive/15 text-destructive",
};

const AdminReviews = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>("all");
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [replyId, setReplyId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState("");

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("product_reviews")
      .select("*, product:products(name)")
      .order("created_at", { ascending: false });
    setReviews((data ?? []) as Review[]);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (id: string, status: string) => {
    const { error } = await supabase.from("product_reviews").update({ status }).eq("id", id);
    if (error) { toast.error(error.message); return; }
    toast.success(`Review ${status}`);
    load();
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    const { error } = await supabase.from("product_reviews").delete().eq("id", deleteId);
    if (error) { toast.error(error.message); return; }
    toast.success("Review deleted");
    setDeleteId(null);
    load();
  };

  const submitReply = async () => {
    if (!replyId) return;
    const { error } = await supabase
      .from("product_reviews")
      .update({ admin_reply: replyText.trim() || null })
      .eq("id", replyId);
    if (error) { toast.error(error.message); return; }
    toast.success("Reply saved");
    setReplyId(null);
    setReplyText("");
    load();
  };

  const filtered = reviews.filter((r) => filter === "all" || r.status === filter);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">Reviews</h1>
          <p className="text-muted-foreground">Manage customer product reviews</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <Skeleton className="h-40 w-full" />
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground border-0 shadow-soft">
          No reviews to show.
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => (
            <Card key={r.id} className="p-4 border-0 shadow-soft">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold">{r.customer_name}</p>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${statusStyles[r.status] ?? ""}`}>
                      {r.status}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    on <span className="font-medium text-foreground/80">{r.product?.name ?? "—"}</span>
                    {" • "}{new Date(r.created_at).toLocaleString()}
                  </p>
                  <div className="mt-2"><Stars value={r.rating} size={14} /></div>
                  {r.comment && <p className="text-sm mt-2 whitespace-pre-line">{r.comment}</p>}
                  {r.image_url && (
                    <img src={r.image_url} alt="" className="mt-2 h-24 w-24 rounded-lg object-cover border" loading="lazy" />
                  )}
                  {r.admin_reply && (
                    <div className="mt-3 rounded-lg bg-primary/5 border-l-2 border-primary p-2.5">
                      <p className="text-[11px] font-semibold text-primary inline-flex items-center gap-1.5">
                        <MessageSquare className="h-3 w-3" /> Admin reply
                      </p>
                      <p className="text-sm mt-1 whitespace-pre-line">{r.admin_reply}</p>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {r.status !== "approved" && (
                    <Button size="sm" variant="outline" onClick={() => updateStatus(r.id, "approved")}>
                      <Check className="h-4 w-4" /> Approve
                    </Button>
                  )}
                  {r.status !== "rejected" && (
                    <Button size="sm" variant="outline" onClick={() => updateStatus(r.id, "rejected")}>
                      <X className="h-4 w-4" /> Reject
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => { setReplyId(r.id); setReplyText(r.admin_reply ?? ""); }}>
                    <Reply className="h-4 w-4" /> Reply
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setDeleteId(r.id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this review?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!replyId} onOpenChange={(o) => { if (!o) { setReplyId(null); setReplyText(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reply to review</AlertDialogTitle>
            <AlertDialogDescription>Your reply will appear publicly below the customer's review.</AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            rows={4}
            placeholder="Thank you for shopping with Rupshoili..."
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={submitReply}>Save reply</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminReviews;
